// TESSERAX — persistance & classement.
//
// Record personnel + pseudo : localStorage (marche partout, hors-ligne).
// Classement quotidien partagé : API Cloudflare Pages Functions + D1.
// Tout est en dégradation gracieuse : si l'API est injoignable, le jeu reste
// parfaitement jouable, seul le classement affiche « indisponible ».

import { type ModeRecords, type RecordOutcome, parseRecords, serializeRecords, bestFor, applyRecord } from '../core/records';
import { type MetricRegistry, parseMetrics, serializeMetrics, bumpMetric, metricsDelta } from '../core/metrics';

const LS_BEST = 'tesserax:best';
const LS_RECORDS = 'tesserax:records';
const LS_NAME = 'tesserax:name';
const LS_PID = 'tesserax:pid';
const LS_METRICS = 'tesserax:metrics';        // agrégat LOCAL (toujours, privé)
const LS_METRICS_SENT = 'tesserax:metrics_sent'; // dernier état ENVOYÉ (pour n'expédier que le delta)
const LS_TELEMETRY = 'tesserax:telemetry';    // opt-in d'envoi réseau ('1' = activé ; off par défaut)
const LS_PENDING_SCORES = 'tesserax:pending_scores'; // file de scores à rejouer (échec réseau)

export interface LbEntry { n: string; s: number; }
export interface LbResult { list: LbEntry[]; idx: number; all: LbEntry[]; allIdx: number; total: number; allTotal: number; globals: WorldStats | null; verified?: boolean; }
export interface WorldStats { games: number; lines: number; points: number; today?: number; players?: number; }
export interface Profile { found: boolean; name: string; bestScore: number; runs: number; lines: number; rank: number; totalPlayers: number; achievements: string[]; rarity: Record<string, number>; }
// Allure du leader d'une graine (rail « vs #1 ») : trajectoire [tMs, score] reconstituée.
export interface PaceLeader { name: string; score: number; pace: [number, number][]; }
export interface ReplayLeader { name: string; score: number; replay: string; }
// Rejeu PARTAGÉ par lien court : journal stocké côté D1 + métadonnées de lecture (self-check).
export interface SharedReplay { name: string; score: number; mode: string; replay: string; }

function lsGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function lsSet(key: string, val: string): void {
  try { localStorage.setItem(key, val); } catch { /* quota / private mode : on ignore */ }
}

// Identifiant opaque (uuid si dispo, sinon repli temps+aléa). Centralisé : pid + clés d'idempotence.
function genId(): string {
  try { if ((crypto as any)?.randomUUID) return (crypto as any).randomUUID(); } catch { /* ignore */ }
  return 'p-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

/* ----- record personnel + pseudo (local) ----- */
export function getBest(): number {
  const v = lsGet(LS_BEST);
  return v ? (parseInt(v, 10) || 0) : 0;
}
export function setBest(n: number): void { lsSet(LS_BEST, String(n)); }

/* ----- records PERSONNELS par mode (local) -----
   Registre { mode: meilleur_score } sérialisé en JSON. Garde le record GLOBAL ci-dessus
   intact (classement / profil) ; ce registre alimente la boucle « bats ton record » par
   mode (cf. core/records). Dégradation gracieuse : parse tolérant, écriture try/catch. */
export function getRecords(): ModeRecords { return parseRecords(lsGet(LS_RECORDS)); }
export function setRecords(r: ModeRecords): void { lsSet(LS_RECORDS, serializeRecords(r)); }
export function getModeBest(mode: string): number { return bestFor(getRecords(), mode); }
// Applique un score de fin de partie au registre et persiste s'il bat la marque du mode.
// Renvoie l'issue (isRecord / prev) pour la célébration côté main.ts.
export function saveModeBest(mode: string, score: number): RecordOutcome {
  const out = applyRecord(getRecords(), mode, score);
  if (out.isRecord) setRecords(out.records);
  return out;
}

export function getName(): string { return lsGet(LS_NAME) || ''; }
export function setName(n: string): void { lsSet(LS_NAME, n); }

// Identité ANONYME stable (uuid en localStorage) : clé d'un profil joueur côté D1
// (progression durable, classement par joueur, succès partagés). Créée à la
// volée, sans authentification ; absente en mode privé → on dégrade simplement.
// Cache MÉMOIRE du pid : si localStorage est indisponible (mode privé / quota), getPid()
// régénérait un UUID DIFFÉRENT à CHAQUE appel → chaque requête d'une même session envoyait
// un pid distinct (profil joueur fragmenté côté D1). On fige donc le pid pour la session
// (ROB-SAVE-004) : stable même sans localStorage. lsSet reste best-effort pour la persistance.
let _pid: string | null = null;
export function getPid(): string {
  if (_pid) return _pid;
  const stored = lsGet(LS_PID);
  if (stored && /^[A-Za-z0-9-]{8,40}$/.test(stored)) { _pid = stored; return _pid; }
  _pid = genId();
  lsSet(LS_PID, _pid);   // best-effort : si ça échoue, le cache mémoire garde le pid stable
  return _pid;
}

/* ----- MÉTRIQUES DE MOTIVATION (MOT-METRICS-002) ----- */
// Agrégat LOCAL : toujours actif, privé (ne quitte jamais l'appareil), sans consentement —
// donne une baseline mesurable sur l'appareil. La TÉLÉMÉTRIE réseau est OPT-IN (off par défaut).
export function getMetrics(): MetricRegistry { return parseMetrics(lsGet(LS_METRICS)); }
// Dump JSON lisible de la baseline locale (debug / export manuel).
export function dumpMetrics(): string { return JSON.stringify(getMetrics(), null, 2); }
// Incrémente un compteur local (clé bornée à l'allowlist core/metrics). Best-effort.
export function track(key: string, n = 1): void {
  try { lsSet(LS_METRICS, serializeMetrics(bumpMetric(getMetrics(), key, n))); } catch { /* ignore */ }
}
// Opt-in d'envoi réseau (off par défaut → privacy-first ; le local marche sans).
export function getTelemetryOptIn(): boolean { return lsGet(LS_TELEMETRY) === '1'; }
export function setTelemetryOptIn(on: boolean): void { lsSet(LS_TELEMETRY, on ? '1' : '0'); }

// Envoie le DELTA des compteurs (depuis le dernier envoi) vers /api/telemetry, SEULEMENT si
// le joueur a opté pour le partage. Best-effort : aucune attente bloquante, erreurs avalées,
// timeout court. Sur succès, mémorise l'état envoyé (offline → ré-accumulé, expédié plus tard).
export async function flushMetrics(): Promise<void> {
  if (!getTelemetryOptIn()) return;
  const cur = getMetrics();
  const sent = parseMetrics(lsGet(LS_METRICS_SENT));
  const delta = metricsDelta(cur, sent);
  if (!Object.keys(delta).length) return;
  try {
    // fetchJson lève si !ok / timeout → on n'avance le curseur QUE sur succès (sinon delta ré-accumulé).
    await fetchJson(`${API_BASE}/telemetry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pid: getPid(), ev: delta }),
    }, 5000);
    lsSet(LS_METRICS_SENT, serializeMetrics(cur));
  } catch { /* hors-ligne / API absente : on garde le delta pour le prochain flush */ }
}

/* ----- classement quotidien partagé (réseau) ----- */
// En production (Cloudflare Pages) les fonctions sont servies à la même
// origine sous /api. En dev Vite sans wrangler, les appels échouent → fallback.
const API_BASE = '/api';

// Récupère + parse en JSON sous un SEUL timeout qui couvre AUSSI le .json() : l'AbortController
// seul n'annulait que le fetch, pas le parsing → une réponse arrivée juste avant l'échéance
// pouvait être parsée et appliquée APRÈS coup (réponse stale). On fait courir fetch+json contre
// un timer ; à l'échéance on abort ET on rejette. Lève en cas d'erreur réseau/HTTP/timeout :
// chaque appelant catch → null (dégradation gracieuse inchangée). (ROB-ASYNC-002)
async function fetchJson(url: string, opts: RequestInit, ms: number): Promise<any> {
  const c = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, rej) => { timer = setTimeout(() => { c.abort(); rej(new Error('timeout')); }, ms); });
  const work = (async () => {
    const r = await fetch(url, { ...opts, signal: c.signal });
    if (!r.ok) throw new Error('http ' + r.status);
    return r.json();
  })();
  try { return await Promise.race([work, deadline]); }
  finally { clearTimeout(timer); }
}

// Nombre fini sûr (réponses serveur) : NaN/Infinity/non-nombre → fallback. (ROB-NET-001)
function num(v: unknown, fallback = 0): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}
function intGE0(v: unknown, fallback = 0): number {
  const n = num(v, fallback); return n >= 0 ? Math.floor(n) : fallback;
}

// Récupère un classement. `query` est une chaîne de paramètres (ex. `seed=…`,
// `scope=all`, `mode=free`). Dégradation gracieuse : null si erreur réseau (hors-ligne
// ou API indisponible), [] si la liste est réellement vide (aucun score encore).
// L'appelant peut ainsi distinguer « hors-ligne » de « personne n'a encore joué ».
export async function fetchLeaderboard(query: string): Promise<LbEntry[] | null> {
  // Rétro-compat : un appel historique passait juste la graine.
  const q = /[=&]/.test(query) ? query : `seed=${encodeURIComponent(query)}`;
  try {
    const data = await fetchJson(`${API_BASE}/leaderboard?${q}`, {}, 5000);
    return Array.isArray(data?.list) ? data.list : [];
  } catch {
    return null;
  }
}

// Compteurs mondiaux (parties jouées, lignes effacées…) pour l'écran d'accueil.
export async function fetchStats(): Promise<WorldStats | null> {
  try {
    const d = await fetchJson(`${API_BASE}/stats`, {}, 5000);
    if (typeof d?.games !== 'number') return null;
    return { games: num(d.games), lines: num(d.lines), points: num(d.points), today: num(d.today), players: num(d.players) };
  } catch {
    return null;
  }
}

// Soumet un score (tous modes). Renvoie le top contextuel + le top éternel + les
// rangs et compteurs mondiaux, ou null si indisponible. `pid` + `ach` alimentent
// le profil joueur anonyme et les succès partagés (best-effort côté serveur).
// Soumissions de score EN COURS, indexées par clé logique : un double-clic / re-render qui
// relance la MÊME soumission renvoie la promesse déjà en vol au lieu d'un 2ᵉ POST (ROB-ASYNC-003).
const _submitInflight = new Map<string, Promise<LbResult | null>>();
// Clé LOGIQUE stable d'une soumission (dédup en vol ci-dessus + dédoublonnage de la file de rejeu).
// Elle NE sert PLUS telle quelle d'Idempotency-Key : deux runs DISTINCTES aux stats identiques
// (ex. deux morts instantanées à 0 le même jour) produisent la même clé logique, et la seconde
// serait avalée comme doublon côté serveur. L'Idempotency-Key envoyée = clé logique + NONCE par
// soumission (cf. submitScore) — unique entre runs, identique entre retrys d'une même soumission.
function submitKey(seed: string, score: number, dur: number, lines: number, mode: string, pid: string): string {
  return [seed, mode, pid, score | 0, dur | 0, lines | 0].join('|');
}

function parseLbResult(data: any): LbResult | null {
  if (!data || !Array.isArray(data.list)) return null;
  const cleanList = (l: unknown): LbEntry[] => Array.isArray(l) ? l.filter((e: any) => e && typeof e.n === 'string').map((e: any) => ({ n: String(e.n), s: intGE0(e.s) })) : [];
  return {
    list: cleanList(data.list),
    idx: Number.isInteger(data.idx) ? data.idx : -1,
    all: cleanList(data.all),
    allIdx: Number.isInteger(data.allIdx) ? data.allIdx : -1,
    total: intGE0(data.total),
    allTotal: intGE0(data.allTotal),
    globals: data.globals && typeof data.globals.games === 'number'
      ? { games: num(data.globals.games), lines: num(data.globals.lines), points: num(data.globals.points), today: num(data.globals.today), players: num(data.globals.players) }
      : null,
    verified: data.verified === true,
  };
}

export async function submitScore(seed: string, name: string, score: number, dur: number, lines = 0, mode = 'daily', pid = '', ach: string[] = [], pace = '', replay = ''): Promise<LbResult | null> {
  const key = submitKey(seed, score, dur, lines, mode, pid);
  const existing = _submitInflight.get(key);
  if (existing) return existing;   // soumission identique déjà en vol → on partage la promesse
  // NONCE par soumission : généré UNE fois à la CRÉATION de la soumission, puis réutilisé tel quel
  // par tous ses retrys (stocké avec l'entrée de la file de rejeu) → le serveur dédoublonne bien les
  // renvois, sans jamais confondre deux runs distinctes aux stats identiques. Math.random suffit
  // (pas d'enjeu de déterminisme hors gameplay) ; charset [a-z0-9] ⊂ assainissement serveur
  // (cleanIdem), longueur totale ≤ ~90 < borne serveur (120).
  const idem = key + '|' + Math.random().toString(36).slice(2, 10);
  const p = _doSubmit(key, idem, seed, name, score, dur, lines, mode, pid, ach, pace, replay)
    .finally(() => _submitInflight.delete(key));
  _submitInflight.set(key, p);
  return p;
}

async function _doSubmit(key: string, idem: string, seed: string, name: string, score: number, dur: number, lines: number, mode: string, pid: string, ach: string[], pace: string, replay: string): Promise<LbResult | null> {
  // `pace` (trajectoire compacte) et `replay` (journal COMPLET pour l'anti-triche par re-simulation)
  // ne sont joints que s'ils existent → best-effort serveur (champs optionnels, bornés côté API).
  const payload: Record<string, unknown> = { seed, name, score, dur, lines, mode, pid, ach };
  if (pace) payload.pace = pace;
  if (replay) payload.replay = replay;
  try {
    const data = await fetchJson(`${API_BASE}/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idem },
      body: JSON.stringify(payload),
    }, 6000);
    const res = parseLbResult(data);
    if (res) clearPendingScore(key);   // succès → purge une éventuelle copie en file
    return res;
  } catch (e) {
    // 4xx = REJET DÉFINITIF par la validation serveur (score/graine/débit — ex. défi antidaté) :
    // rejouer ne servira jamais, on ne met PAS en file. Hors-ligne / timeout / 5xx : on met la
    // soumission en FILE pour rejeu au prochain lancement, AVEC sa clé d'idempotence (même
    // Idempotency-Key au renvoi → pas de doublon serveur). Le score n'est plus silencieusement perdu.
    if (!(e instanceof Error && /^http 4/.test(e.message))) queuePendingScore(key, idem, payload);
    return null;
  }
}

/* ----- FILE DE REJEU DES SCORES (offline → réessai au lancement) (ROB-NET-002) ----- */
// `key` = clé LOGIQUE (dédoublonnage de la file) ; `idem` = Idempotency-Key COMPLÈTE (clé + nonce)
// envoyée au serveur — stockée AVEC l'entrée pour que chaque retry renvoie exactement la même
// (c'est tout l'intérêt). `idem` optionnel : une vieille entrée (avant nonce) retombe sur `key`.
interface PendingScore { key: string; idem?: string; payload: Record<string, unknown>; t: number; }
const MAX_PENDING = 5;
function readPending(): PendingScore[] {
  try { const a = JSON.parse(lsGet(LS_PENDING_SCORES) || '[]'); return Array.isArray(a) ? a : []; } catch { return []; }
}
function writePending(a: PendingScore[]): void { lsSet(LS_PENDING_SCORES, JSON.stringify(a.slice(-MAX_PENDING))); }
function queuePendingScore(key: string, idem: string, payload: Record<string, unknown>): void {
  const a = readPending().filter(p => p.key !== key);   // dédoublonne par clé logique
  a.push({ key, idem, payload, t: Date.now() });
  writePending(a);
}
function clearPendingScore(key: string): void {
  const a = readPending().filter(p => p.key !== key);
  if (a.length) writePending(a); else { try { localStorage.removeItem(LS_PENDING_SCORES); } catch { /* ignore */ } }
}
// Rejoue les soumissions en attente (à appeler au lancement, best-effort, non bloquant). Même
// Idempotency-Key (celle STOCKÉE avec l'entrée) → le serveur dédoublonne si le POST initial
// avait en fait abouti.
export async function flushPendingScores(): Promise<void> {
  const a = readPending();
  if (!a.length) return;
  for (const p of a) {
    try {
      const data = await fetchJson(`${API_BASE}/score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': p.idem || p.key },
        body: JSON.stringify(p.payload),
      }, 6000);
      if (parseLbResult(data)) clearPendingScore(p.key);
    } catch (e) {
      // 4xx = rejet DÉFINITIF (validation serveur, ex. graine de défi désormais trop vieille) :
      // on purge l'entrée, la rejouer indéfiniment ne servirait à rien.
      if (e instanceof Error && /^http 4/.test(e.message)) clearPendingScore(p.key);
      /* sinon toujours hors-ligne : on retentera au prochain lancement */
    }
  }
}

// REJEU PARTAGÉ par lien COURT (/r/<id>). Stocke le journal côté D1 et renvoie son `id` opaque,
// d'où un lien de longueur constante (~30 car.) qui contourne la limite d'URL (~32 Ko) du lien
// INLINE des très longues parties. null si indisponible (dev sans /api, hors-ligne, table non
// migrée) → l'appelant retombe sur le lien inline (dégradation gracieuse, le partage marche toujours).
export async function shareReplay(replay: string, score: number, name = ''): Promise<string | null> {
  if (!replay) return null;
  try {
    // seed + mode sont LUS du journal côté serveur (autorité) → on n'envoie que le strict utile.
    const d = await fetchJson(`${API_BASE}/replay`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ replay, score: score | 0, name }),
    }, 6000);
    return d && typeof d.id === 'string' && /^[A-Za-z0-9]{1,24}$/.test(d.id) ? d.id : null;
  } catch {
    return null;
  }
}

// Récupère un rejeu PARTAGÉ par son `id` (cible du lien /r/<id> → ?r=<id>). Le consommateur
// le décode/valide (core/replay) avant lecture. null si indisponible (id mort, table absente,
// réseau) → l'app reste sur l'accueil (dégradation gracieuse, jamais bloquant).
export async function fetchSharedReplay(id: string): Promise<SharedReplay | null> {
  if (!id) return null;
  try {
    const d = await fetchJson(`${API_BASE}/replay?id=${encodeURIComponent(id)}`, {}, 6000);
    if (!d || typeof d.replay !== 'string' || !d.replay) return null;
    return { name: String(d.name || ''), score: intGE0(d.score), mode: String(d.mode || ''), replay: d.replay };
  } catch {
    return null;
  }
}

// Récupère le profil joueur (rang par joueur, effectif, succès + rareté). Null si
// indisponible (dev sans /api, hors-ligne, table non migrée) → dégradation propre.
export async function fetchProfile(pid: string): Promise<Profile | null> {
  if (!pid) return null;
  try {
    const d = await fetchJson(`${API_BASE}/profile?pid=${encodeURIComponent(pid)}`, {}, 5000);
    if (typeof d?.totalPlayers !== 'number') return null;
    return {
      found: !!d.found, name: String(d.name || ''), bestScore: intGE0(d.bestScore), runs: intGE0(d.runs), lines: intGE0(d.lines),
      rank: Number.isInteger(d.rank) ? d.rank : -1, totalPlayers: intGE0(d.totalPlayers),
      achievements: Array.isArray(d.achievements) ? d.achievements.filter((x: unknown) => typeof x === 'string') : [],
      rarity: d.rarity && typeof d.rarity === 'object' && !Array.isArray(d.rarity) ? d.rarity : {},
    };
  } catch {
    return null;
  }
}

// Allure du leader (rang 1 par défaut) d'une graine du jour → rail « vs #1 ». La
// trajectoire arrive en JSON compact [[t,s],…] : on la re-parse en VÉRIFIANT la forme
// (paires finies, taille bornée), sinon on l'ignore. Null si indisponible (dev sans /api,
// table non migrée, aucune run) → le rail retombe sur l'allure LOCALE. Jamais bloquant.
export async function getPace(seed: string, rank = 1): Promise<PaceLeader | null> {
  if (!seed) return null;
  try {
    const d = await fetchJson(`${API_BASE}/pace?seed=${encodeURIComponent(seed)}&rank=${rank | 0}`, {}, 5000);
    if (!d || typeof d.pace !== 'string' || !d.pace) return null;
    let raw: unknown;
    try { raw = JSON.parse(d.pace); } catch { return null; }
    if (!Array.isArray(raw) || raw.length === 0 || raw.length > 2000) return null;
    const pace: [number, number][] = [];
    for (const p of raw) {
      if (!Array.isArray(p) || p.length < 2) return null;
      const t = Number(p[0]), s = Number(p[1]);
      if (!Number.isFinite(t) || !Number.isFinite(s) || t < 0 || s < 0) return null;
      pace.push([t, s]);
    }
    const score = num(d.score) || (pace.length ? pace[pace.length - 1][1] : 0);
    if (score <= 0) return null;
    return { name: String(d.name || ''), score, pace };
  } catch {
    return null;
  }
}

// Journal COMPLET du leader (rang) du défi du jour → source du PUITS FANTÔME (M3.2). Le consommateur
// le décode/valide (core/replay) puis le rejoue dans core/sim. null si indisponible (table absente,
// graine vide, run sans journal stocké, réseau) → pas de fantôme (dégradation gracieuse).
export async function getReplay(seed: string, rank = 1): Promise<ReplayLeader | null> {
  if (!seed) return null;
  try {
    const d = await fetchJson(`${API_BASE}/replay?seed=${encodeURIComponent(seed)}&rank=${rank | 0}`, {}, 6000);
    if (!d || typeof d.replay !== 'string' || !d.replay) return null;
    return { name: String(d.name || ''), score: intGE0(d.score), replay: d.replay };
  } catch {
    return null;
  }
}
